import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Show, SignInButton, useAuth, useUser } from "../../hooks/useAuthCompat";
import { useQuery } from "@tanstack/react-query";
import { Menu, LifeBuoy, ShieldCheck, ChevronDown, Library, UserCircle, CreditCard, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "../ui/sheet";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../ui/dropdown-menu";
import { useMe } from "../../hooks/useMe";
import { useApi } from "../../hooks/useApi";
import { Logo } from "../Logo";
import { HeaderSearch } from "./HeaderSearch";
import { CartButton, CartDrawer } from "../cart/CartDrawer";

const BASE_LINKS = [
  { to: "/", label: "Home" },
  { to: "/listen", label: "Podcast" },
  { to: "/books", label: "Books" },
  { to: "/themuscleschool", label: "The Muscle School" },
];

export function Navbar() {
  const navigate = useNavigate();
  const { isSignedIn, signOut } = useAuth();
  const { user } = useUser();
  const { data: me } = useMe();
  const api = useApi();
  const { data: goldMembership } = useQuery({
    queryKey: ["gold-membership"], queryFn: async () => (await api.get("/gold/membership")).data, enabled: !!isSignedIn,
  });
  const [open, setOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const goldLink = goldMembership?.product_type ? { to: "/vault", label: "Gold Vault" } : { to: "/gold", label: "Join Gold" };
  const LINKS = [...BASE_LINKS, goldLink];

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-black border-b border-white/10" data-testid="main-navbar">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8">
        {/* Row 1: wordmark | search (desktop) | account / menu */}
        <div className="flex items-center justify-between gap-4 h-16">
          <Link to="/" data-testid="nav-logo" aria-label="Endure Beyond — home" className="shrink-0">
            <Logo variant="wordmark-light" className="h-4 md:h-5 w-auto" />
          </Link>

          <div className="hidden md:flex flex-1 justify-center px-6">
            <HeaderSearch className="w-full max-w-[460px]" testId="desktop-search-input" />
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <CartButton onClick={() => setCartOpen(true)} />
            <button data-testid="nav-support-button" onClick={() => navigate("/support")} className="hidden lg:block text-white/70 hover:text-white transition-colors duration-200">
              <LifeBuoy className="w-5 h-5" />
            </button>
            {me?.role === "admin" && (
              <button data-testid="nav-admin-button" onClick={() => navigate("/admin")} className="hidden lg:block text-brandAccent hover:text-white transition-colors duration-200">
                <ShieldCheck className="w-5 h-5" />
              </button>
            )}
            <Show when="signed-out">
              <SignInButton>
                <button data-testid="nav-signin-button" className="px-4 py-2 text-sm bg-white text-black hover:bg-white/80 transition-colors duration-200">
                  Sign In
                </button>
              </SignInButton>
            </Show>
            <Show when="signed-in">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-account-menu-button" className="flex items-center gap-1.5 text-white/80 hover:text-white transition-colors duration-200">
                    {user?.imageUrl ? (
                      <img src={user.imageUrl} alt="" className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <UserCircle className="w-7 h-7" />
                    )}
                    <ChevronDown className="w-3.5 h-3.5 hidden md:block" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-black border-white/15 text-white w-56" data-testid="nav-account-menu">
                  <DropdownMenuItem data-testid="account-menu-library" onClick={() => navigate("/library")} className="gap-2 focus:bg-white/10 focus:text-white cursor-pointer">
                    <Library className="w-4 h-4" /> My Library
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="account-menu-membership" onClick={() => navigate("/account")} className="gap-2 focus:bg-white/10 focus:text-white cursor-pointer">
                    <CreditCard className="w-4 h-4" /> Account / Membership
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem data-testid="account-menu-signout" onClick={async () => { await signOut(); navigate("/"); }} className="gap-2 focus:bg-white/10 focus:text-white cursor-pointer">
                    <LogOut className="w-4 h-4" /> Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </Show>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="nav-mobile-menu-button" className="lg:hidden text-white">
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-black border-white/10 text-white w-72">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <SheetDescription className="sr-only">Links to the main sections of Endure Beyond</SheetDescription>
                <div className="flex flex-col gap-5 mt-10">
                  {LINKS.map((l) => (
                    <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`} className="text-base uppercase tracking-wide text-white/80 hover:text-white">
                      {l.label}
                    </Link>
                  ))}
                  <Link to="/support" onClick={() => setOpen(false)} className="text-base uppercase tracking-wide text-white/80 hover:text-white">Support</Link>
                  {me?.role === "admin" && (
                    <Link to="/admin" onClick={() => setOpen(false)} className="text-base uppercase tracking-wide text-brandAccent">Administration Studio</Link>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Row 2 (mobile/tablet): full-width search */}
        <div className="md:hidden pb-3">
          <HeaderSearch className="w-full" testId="mobile-search-input" />
        </div>

        {/* Row 2 (desktop): primary navigation */}
        <nav className="hidden lg:flex items-center gap-7 h-12 border-t border-white/10">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide uppercase transition-colors duration-200 ${isActive ? "text-white" : "text-white/60 hover:text-white"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
      </div>
      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
    </header>
  );
}
